import React, { useState, useEffect } from 'react';
import { X, Truck, Save, AlertTriangle, History, ChevronRight, Loader2 } from 'lucide-react';
import RollingOdometerInput from '../shared/RollingOdometerInput';
import { api } from '../../utils/api';
import { toast } from '../ui/simple-toaster';

/**
 * MyVehicleModal - For owner/drivers to manage their single vehicle
 * Owner/drivers can only have ONE vehicle at a time.
 * When changing to a new vehicle, the old one is archived (kept for history).
 */
const MyVehicleModal = ({ 
  isOpen, 
  onClose, 
  vehicle: propVehicle,
  onVehicleUpdated
}) => {
  const [mode, setMode] = useState('view'); // view, edit, change
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [vehicle, setVehicle] = useState(propVehicle);
  
  // Form state for editing
  const [formData, setFormData] = useState({
    rego: '',
    make: '',
    model: '',
    year: '',
    gvm_kg: 0,
    gcm_kg: 0,
    odometer: 0,
    primary_production_eligible: false
  });
  
  // Load owner/driver status to get vehicle on mount
  useEffect(() => {
    const loadOwnerDriverVehicle = async () => {
      if (propVehicle) {
        setVehicle(propVehicle);
        setInitialLoading(false);
        return;
      }
      
      try {
        setInitialLoading(true);
        const data = await api('/owner-driver/status');
        if (data.vehicle) {
          setVehicle(data.vehicle);
        }
      } catch (err) {
        // Not an owner/driver or no vehicle
        console.log('Could not load owner/driver vehicle:', err);
      } finally {
        setInitialLoading(false);
      }
    };
    
    if (isOpen) {
      loadOwnerDriverVehicle();
    }
  }, [isOpen, propVehicle]);
  
  // Reset form when vehicle changes
  useEffect(() => {
    if (vehicle) {
      setFormData({
        rego: vehicle.rego || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year || '',
        gvm_kg: vehicle.gvm_kg || vehicle.weight_kg || 0,
        gcm_kg: vehicle.gcm_kg || 0,
        odometer: vehicle.last_odometer || 0,
        primary_production_eligible: vehicle.primary_production_eligible || false
      });
    }
    setMode('view');
  }, [vehicle, isOpen]);
  
  // Load vehicle history
  const loadHistory = async () => {
    try {
      const data = await api('/owner-driver/vehicle-history');
      setHistory(data.vehicles || []);
      setShowHistory(true);
    } catch (err) {
      toast.error('Failed to load vehicle history');
    }
  };
  
  // Handle save (update existing vehicle)
  const handleSave = async () => {
    if (!vehicle) return;
    setLoading(true);
    
    try {
      await api('/owner-driver/vehicle', {
        method: 'PUT',
        body: JSON.stringify({
          rego: formData.rego,
          make: formData.make,
          model: formData.model,
          gvm_kg: parseInt(formData.gvm_kg) || 0,
          gcm_kg: parseInt(formData.gcm_kg) || 0,
          odometer: parseInt(formData.odometer) || 0,
          primary_production_eligible: formData.primary_production_eligible,
          is_new_vehicle: false
        })
      });
      
      toast.success('Vehicle updated');
      onVehicleUpdated?.();
      setMode('view');
    } catch (err) {
      toast.error('Failed to update vehicle: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle change to new vehicle
  const handleChangeVehicle = async () => {
    setLoading(true);
    
    try {
      const result = await api('/owner-driver/vehicle', {
        method: 'PUT',
        body: JSON.stringify({
          rego: formData.rego,
          make: formData.make,
          model: formData.model,
          gvm_kg: parseInt(formData.gvm_kg) || 0,
          gcm_kg: parseInt(formData.gcm_kg) || 0,
          odometer: parseInt(formData.odometer) || 0,
          primary_production_eligible: formData.primary_production_eligible,
          is_new_vehicle: true
        })
      });
      
      toast.success(`Vehicle changed to ${formData.rego}`);
      onVehicleUpdated?.();
      setMode('view');
      onClose();
    } catch (err) {
      toast.error('Failed to change vehicle: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  // Show loading state while fetching vehicle
  if (initialLoading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl p-8">
          <div className="flex flex-col items-center justify-center gap-4">
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
            <p className="text-slate-400">Loading vehicle...</p>
          </div>
        </div>
      </div>
    );
  }
  
  const gvm = parseInt(formData.gvm_kg) || 0;
  const regime = gvm >= 12000 ? 'FRHV' : gvm >= 4500 ? 'HV' : 'LV';
  const regimeColor = regime === 'FRHV' ? 'text-blue-400' : regime === 'HV' ? 'text-amber-400' : 'text-emerald-400';
  
  // Vehicle History View
  if (showHistory) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl max-h-[90vh] overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setShowHistory(false)} className="p-1 hover:bg-slate-700 rounded-lg">
                <ChevronRight className="w-5 h-5 text-slate-400 rotate-180" />
              </button>
              <h3 className="text-lg font-bold text-white">Vehicle History</h3>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            {history.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No vehicle history</p>
            ) : (
              <div className="space-y-3">
                {history.map((v, i) => (
                  <div 
                    key={v.vehicle_id} 
                    className={`p-3 rounded-xl border ${v.active ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-700/50 border-slate-600'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-white">{v.rego}</span>
                      {v.active && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">Current</span>}
                      {!v.active && <span className="text-xs bg-slate-600 text-slate-300 px-2 py-0.5 rounded">Archived</span>}
                    </div>
                    <div className="text-sm text-slate-400">
                      <p>{v.make} {v.model}</p>
                      <p>GVM: {v.weight_kg?.toLocaleString()} kg</p>
                      {v.archived_at && (
                        <p className="text-xs mt-1">Archived: {new Date(v.archived_at).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Change Vehicle Form
  if (mode === 'change') {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800">
            <h3 className="text-lg font-bold text-white">Change Vehicle</h3>
            <button onClick={() => setMode('view')} className="p-2 hover:bg-slate-700 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          
          <div className="p-4 space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-200">
                  <strong>Changing Vehicle</strong>
                  <p className="mt-1">Your current vehicle <strong>{vehicle?.rego}</strong> will be archived for compliance records. Enter details for your new vehicle.</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Registration</label>
                <input
                  type="text"
                  value={formData.rego}
                  onChange={(e) => setFormData({ ...formData, rego: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  placeholder="ABC123"
                  data-testid="my-vehicle-rego"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Make</label>
                  <input
                    type="text"
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    placeholder="Toyota"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Model</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    placeholder="Hilux"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">GVM (kg)</label>
                  <input
                    type="number"
                    value={formData.gvm_kg}
                    onChange={(e) => setFormData({ ...formData, gvm_kg: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    placeholder="4500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">GCM (kg)</label>
                  <input
                    type="number"
                    value={formData.gcm_kg}
                    onChange={(e) => setFormData({ ...formData, gcm_kg: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    placeholder="7000"
                  />
                </div>
              </div>
              
              <div className="text-center py-2">
                <span className={`text-sm font-medium ${regimeColor}`}>
                  Regime: {regime === 'FRHV' ? 'Fatigue Regulated Heavy Vehicle' : regime === 'HV' ? 'Heavy Vehicle' : 'Light Vehicle'}
                </span>
              </div>
              
              <RollingOdometerInput
                value={formData.odometer}
                onChange={(val) => setFormData({ ...formData, odometer: val })}
                label="Starting Odometer"
                digits={6}
              />
            </div>
          </div>
          
          <div className="p-4 border-t border-slate-700 flex gap-3 sticky bottom-0 bg-slate-800">
            <button
              onClick={() => setMode('view')}
              className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition"
            >
              Cancel
            </button>
            <button
              onClick={handleChangeVehicle}
              disabled={loading || !formData.rego || !formData.gvm_kg}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="my-vehicle-change-confirm"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Confirm Change'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Main View / Edit Mode
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Truck className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">My Vehicle</h3>
              <p className="text-sm text-slate-400">{mode === 'edit' ? 'Edit Details' : 'Vehicle Info'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg" data-testid="my-vehicle-close">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-4 space-y-4">
          {!vehicle ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <p className="text-slate-300">No vehicle registered</p>
              <button
                onClick={() => setMode('change')}
                className="mt-4 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium"
              >
                Add Vehicle
              </button>
            </div>
          ) : mode === 'view' ? (
            <>
              {/* Vehicle Card */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-2xl font-bold text-white">{vehicle.rego}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    regime === 'FRHV' ? 'bg-blue-500/20 text-blue-400' : 
                    regime === 'HV' ? 'bg-amber-500/20 text-amber-400' : 
                    'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {regime}
                  </span>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Make/Model</span>
                    <span className="text-white">{vehicle.make || '—'} {vehicle.model || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">GVM</span>
                    <span className="text-white">{(vehicle.gvm_kg || vehicle.weight_kg)?.toLocaleString()} kg</span>
                  </div>
                  {vehicle.gcm_kg > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">GCM</span>
                      <span className="text-white">{vehicle.gcm_kg?.toLocaleString()} kg</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-400">Odometer</span>
                    <span className="text-white font-mono">{(vehicle.last_odometer || 0).toLocaleString()} km</span>
                  </div>
                  {vehicle.primary_production_eligible && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Primary Production</span>
                      <span className="text-emerald-400">Eligible (160km)</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setMode('edit')}
                  className="py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
                  data-testid="my-vehicle-edit-btn"
                >
                  <Save className="w-4 h-4" />
                  Edit Details
                </button>
                <button
                  onClick={loadHistory}
                  className="py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
                  data-testid="my-vehicle-history-btn"
                >
                  <History className="w-4 h-4" />
                  History
                </button>
              </div>
              
              <button
                onClick={() => {
                  setFormData({
                    rego: '',
                    make: '',
                    model: '',
                    year: '',
                    gvm_kg: 0,
                    gcm_kg: 0,
                    odometer: 0,
                    primary_production_eligible: false
                  });
                  setMode('change');
                }}
                className="w-full py-3 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/50 text-amber-400 rounded-xl font-medium transition"
                data-testid="my-vehicle-change-btn"
              >
                Change to Different Vehicle
              </button>
            </>
          ) : (
            /* Edit Mode */
            <>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Registration</label>
                  <input
                    type="text"
                    value={formData.rego}
                    onChange={(e) => setFormData({ ...formData, rego: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Make</label>
                    <input
                      type="text"
                      value={formData.make}
                      onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">Model</label>
                    <input
                      type="text"
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">GVM (kg)</label>
                    <input
                      type="number"
                      value={formData.gvm_kg}
                      onChange={(e) => setFormData({ ...formData, gvm_kg: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">GCM (kg)</label>
                    <input
                      type="number"
                      value={formData.gcm_kg}
                      onChange={(e) => setFormData({ ...formData, gcm_kg: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-xl">
                  <input
                    type="checkbox"
                    id="primary_production"
                    checked={formData.primary_production_eligible}
                    onChange={(e) => setFormData({ ...formData, primary_production_eligible: e.target.checked })}
                    disabled={gvm < 12000}
                    className="w-4 h-4 rounded border-slate-500 text-emerald-500 focus:ring-emerald-500"
                  />
                  <label htmlFor="primary_production" className="text-sm text-slate-300">
                    Primary Production Eligible (160km radius)
                  </label>
                </div>
                {gvm < 12000 && formData.primary_production_eligible && (
                  <p className="text-xs text-amber-400">Primary Production only available for FRHV (GVM ≥ 12,000 kg)</p>
                )}
                
                <RollingOdometerInput
                  value={formData.odometer}
                  onChange={(val) => setFormData({ ...formData, odometer: val })}
                  label="Current Odometer"
                  digits={6}
                />
              </div>
            </>
          )}
        </div>
        
        {/* Footer for Edit Mode */}
        {mode === 'edit' && (
          <div className="p-4 border-t border-slate-700 flex gap-3 sticky bottom-0 bg-slate-800">
            <button
              onClick={() => setMode('view')}
              className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="my-vehicle-save-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyVehicleModal;
